import React, { useState, FormEvent } from 'react';
import { useAuth } from '@/context/AuthContext';
import styles from './LoginPage.module.css';

const isConnectionError = (msg: string) =>
  msg.toLowerCase().includes('failed to fetch') ||
  msg.toLowerCase().includes('network') ||
  msg.toLowerCase().includes('fetch');

type View = 'login' | 'forgot' | 'forgot-sent';

export default function LoginPage() {
  const { signIn, signUp, resetPassword } = useAuth();
  const [view, setView] = useState<View>('login');
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [connectionError, setConnectionError] = useState(false);

  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setConnectionError(false);
    setLoading(true);

    try {
      const { error: authError } = isSignUp
        ? await signUp(email, password)
        : await signIn(email, password);

      if (authError) {
        if (isConnectionError(authError)) {
          setConnectionError(true);
        } else {
          setError(authError);
        }
      }
    } catch (err: any) {
      const msg = err.message || 'An unexpected error occurred';
      if (isConnectionError(msg)) {
        setConnectionError(true);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = (e: FormEvent) => {
    e.preventDefault();
    setResetEmail(email);
    setResetError(null);
    setView('forgot');
  };

  const handleSendReset = async (e: FormEvent) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      setResetError('Please enter your email address.');
      return;
    }
    setResetLoading(true);
    setResetError(null);
    const { error } = await resetPassword(resetEmail.trim());
    setResetLoading(false);
    if (error) {
      setResetError(error);
    } else {
      setView('forgot-sent');
    }
  };

  if (view === 'forgot') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.branding}>
            <div className={styles.appName}>beryl</div>
            <div className={styles.subtitle}>Your Recovery Buddy</div>
          </div>

          <h1 className={styles.title}>Reset Password</h1>
          <p className={styles.forgotDesc}>
            Enter the email on your account and we'll send you a link to set a new password.
          </p>

          <form className={styles.form} onSubmit={handleSendReset}>
            {resetError ? <div className={styles.error}>{resetError}</div> : null}

            <div className={styles.inputGroup}>
              <label className={styles.label}>Email</label>
              <input
                className={styles.input}
                type="email"
                placeholder="you@example.com"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                autoFocus
                required
              />
            </div>

            <button className={styles.submitButton} type="submit" disabled={resetLoading}>
              {resetLoading ? 'Sending…' : 'Send Reset Link'}
            </button>
          </form>

          <div className={styles.toggle}>
            <button className={styles.toggleButton} onClick={() => setView('login')}>
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'forgot-sent') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.branding}>
            <div className={styles.appName}>beryl</div>
          </div>

          <div className={styles.successBox}>
            <div className={styles.successIcon}>✉</div>
            <h2 className={styles.successTitle}>Check your inbox</h2>
            <p className={styles.successBody}>
              We sent a password reset link to <strong>{resetEmail}</strong>.
              Click the link in that email to set a new password.
            </p>
            <p className={styles.successNote}>
              It may take a minute or two to arrive. Check your spam folder if you don't see it.
            </p>
          </div>

          <button
            className={styles.submitButton}
            style={{ marginTop: 8 }}
            onClick={() => setView('login')}
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.branding}>
          <div className={styles.appName}>beryl</div>
          <div className={styles.subtitle}>Your Recovery Buddy</div>
        </div>

        <h1 className={styles.title}>
          {isSignUp ? 'Create Account' : 'Welcome Back'}
        </h1>

        <form className={styles.form} onSubmit={handleSubmit}>
          {connectionError ? (
            <div className={styles.connectionError}>
              <div className={styles.connectionErrorTitle}>Cannot reach the server</div>
              <div className={styles.connectionErrorBody}>
                Your Supabase project may be <strong>paused</strong>. Free-tier projects
                auto-pause after a week of inactivity.
              </div>
              <a
                href="https://supabase.com/dashboard"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.connectionErrorLink}
              >
                Open Supabase Dashboard to unpause →
              </a>
            </div>
          ) : null}

          {error && !connectionError ? <div className={styles.error}>{error}</div> : null}

          <div className={styles.inputGroup}>
            <label className={styles.label}>Email</label>
            <input
              className={styles.input}
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className={styles.inputGroup}>
            <div className={styles.labelRow}>
              <label className={styles.label}>Password</label>
              {!isSignUp ? (
                <button
                  type="button"
                  className={styles.forgotLink}
                  onClick={(e) => handleForgot(e as any)}
                >
                  Forgot password?
                </button>
              ) : null}
            </div>
            <input
              className={styles.input}
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>

          <button
            className={styles.submitButton}
            type="submit"
            disabled={loading}
          >
            {loading
              ? 'Please wait...'
              : isSignUp
                ? 'Sign Up'
                : 'Sign In'}
          </button>
        </form>

        <div className={styles.toggle}>
          {isSignUp ? 'Already have an account?' : "Don't have an account?"}
          <button
            className={styles.toggleButton}
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError(null);
              setConnectionError(false);
            }}
          >
            {isSignUp ? 'Sign In' : 'Sign Up'}
          </button>
        </div>

        <p className={styles.disclaimer}>
          beryl is a personal tracking tool, not a medical device. It does not provide medical advice.{' '}
          <a href="/disclaimer" className={styles.disclaimerLink}>Medical Disclaimer</a>
          {' · '}
          <a href="/support" className={styles.disclaimerLink}>Support</a>
        </p>

      </div>
    </div>
  );
}
